import { useEffect } from 'react';

/**
 * Applies tenant/theme-specific metadata to the document head.
 *
 * Replaces react-helmet-async usage in App.js. Since title/description/favicon/og:image
 * derive from REACT_APP_NAME at build time, this runs once per mount.
 *
 * NOTE: This is client-side only. For SEO crawlers (e.g., social previews that do not
 * execute JS), static meta tags in `public/index.html` and per-tenant build artifacts
 * remain the source of truth. TODO: evaluate SSR/pre-render if og:image per tenant
 * must be visible to non-JS crawlers.
 *
 * @param {{ title?: string, description?: string, favicon?: string, ogImage?: string, bodyClassName?: string }} theme
 */
export function useThemeMetaTags(theme) {
  useEffect(() => {
    if (!theme) return;

    if (theme.title) {
      document.title = theme.title;
    }

    if (theme.bodyClassName) {
      document.body.className = theme.bodyClassName;
    }

    setOrCreateMeta('name', 'description', theme.description);
    setOrCreateMeta('property', 'og:image', theme.ogImage);
    setOrCreateLink('icon', theme.favicon, 'image/png');
  }, [theme]);
}

/**
 * Sets the `content` of an existing meta tag or creates one if missing.
 */
function setOrCreateMeta(attr, attrValue, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${attrValue}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * Sets the `href` of an existing link tag or creates one if missing.
 */
function setOrCreateLink(rel, href, type) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  if (type) el.setAttribute('type', type);
  el.setAttribute('href', href);
}
